import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScreenHeader, Surface, Button, IconButton, Avatar, Divider, Icon, FeedbackMsg, inputCss, AlunoSearch, FormField, Dialog } from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL;

function formatarTelefone(valor) {
  valor = valor.replace(/\D/g, '');
  if (valor.length > 11) valor = valor.slice(0, 11);
  if (valor.length > 2) valor = `(${valor.slice(0, 2)})${valor.slice(2)}`;
  if (valor.length > 9) valor = valor.replace(/^(\(\d{2}\))(\d{5})(\d{4}).*/, '$1$2-$3');
  else if (valor.length > 6) valor = valor.replace(/^(\(\d{2}\))(\d{4})(\d{0,4})/, '$1$2-$3');
  return valor;
}

function AlunoModal({ tipo: modalTipo, aluno, allAlunos, onClose, onSaved, isProfessora }) {
  const isCreate = modalTipo === 'criar';
  const isEdit = modalTipo === 'editar';
  const isDetails = modalTipo === 'detalhes';
  const isSenha = modalTipo === 'senha';

  const [form, setForm] = useState({
    nome: aluno?.nome || '', telefone: aluno?.telefone || '', endereco: aluno?.endereco || '',
    login: aluno?.login || '', email: aluno?.email || '', senha: '',
    valor_hora: aluno?.valor_hora || '',
    valor_hora_particular: aluno?.valor_hora_particular ?? '',
    valor_hora_grupo: aluno?.valor_hora_grupo ?? '',
    valor_mensal_fixo: aluno?.valor_mensal_fixo ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function upd(name, value) { setForm(f => ({ ...f, [name]: value })); }

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    const dados = { ...form };
    if (!isProfessora) delete dados.valor_hora;
    if (isCreate) {
      if (!isSenha && !dados.senha) { setMsg('Informe a senha.'); setSaving(false); return; }
      axios.post(`${API_URL}/alunos`, dados, { withCredentials: true })
        .then(() => { onSaved(); onClose(); })
        .catch(err => setMsg(err.response?.data?.erro || 'Erro ao criar.'))
        .finally(() => setSaving(false));
    } else if (isEdit) {
      if (!dados.senha) delete dados.senha;
      axios.put(`${API_URL}/alunos/${aluno.id}`, dados, { withCredentials: true })
        .then(() => { onSaved(); onClose(); })
        .catch(err => setMsg(err.response?.data?.erro || 'Erro ao editar.'))
        .finally(() => setSaving(false));
    } else if (isSenha) {
      axios.post(`${API_URL}/alunos/${aluno.id}/senha`, { senha: form.senha }, { withCredentials: true })
        .then(() => { onSaved(); onClose(); })
        .catch(err => setMsg(err.response?.data?.erro || 'Erro ao alterar.'))
        .finally(() => setSaving(false));
    }
  }

  const title = isCreate ? 'Novo aluno' : isEdit ? `Editar — ${aluno.nome}` : isDetails ? `Detalhes — ${aluno.nome}` : `Senha — ${aluno.nome}`;

  if (isDetails) return (
    <Dialog title={title} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-3)', marginBottom: 'var(--gap-3)' }}>
        {aluno.foto_url
          ? <img src={aluno.foto_url} alt="" style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
          : <Avatar name={aluno.nome} size={52}/>
        }
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-primary)' }}>{aluno.nome}</div>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{aluno.email}</div>
        </div>
      </div>
      <Surface>
        {[
          { label: 'Login', value: aluno.login, mono: true },
          { label: 'Telefone', value: aluno.telefone, mono: true },
          { label: 'Endereço', value: aluno.endereco },
          isProfessora && aluno.valor_mensal_fixo != null && { label: 'Mensal fixo', value: `R$ ${Number(aluno.valor_mensal_fixo).toFixed(2)}`, mono: true },
          isProfessora && !aluno.valor_mensal_fixo && aluno.valor_hora_particular != null && { label: 'Valor particular/h', value: `R$ ${Number(aluno.valor_hora_particular).toFixed(2)}`, mono: true },
          isProfessora && !aluno.valor_mensal_fixo && aluno.valor_hora_grupo != null && { label: 'Valor grupo/h', value: `R$ ${Number(aluno.valor_hora_grupo).toFixed(2)}`, mono: true },
          isProfessora && !aluno.valor_mensal_fixo && aluno.valor_hora_particular == null && aluno.valor_hora_grupo == null && { label: 'Valor/hora', value: `R$ ${Number(aluno.valor_hora || 0).toFixed(2)}`, mono: true },
        ].filter(Boolean).map((f, i) => (
          <React.Fragment key={i}>
            {i > 0 && <Divider/>}
            <div style={{ padding: 'var(--pad-card)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 2 }}>{f.label}</div>
              <div style={{ fontSize: 13.5, color: 'var(--text-primary)', fontFamily: f.mono ? '"Geist Mono", monospace' : 'inherit' }}>{f.value || '—'}</div>
            </div>
          </React.Fragment>
        ))}
      </Surface>
    </Dialog>
  );

  const formFields = isSenha
    ? [{ name: 'senha', label: 'Nova senha', type: 'password', required: true }]
    : [
        { name: 'nome', label: 'Nome completo', type: 'text', required: true },
        { name: 'email', label: 'E-mail (opcional)', type: 'email', required: false },
        { name: 'telefone', label: 'Telefone', type: 'tel', required: true },
        { name: 'endereco', label: 'Endereço', type: 'text', required: true },
        { name: 'login', label: 'Login', type: 'text', required: true },
        ...(isCreate ? [{ name: 'senha', label: 'Senha', type: 'password', required: true }] : [{ name: 'senha', label: 'Nova senha (em branco = manter)', type: 'password' }]),
        ...(isProfessora ? [
          { name: 'valor_hora_particular', label: 'Valor aula particular (R$/h)', type: 'number' },
          { name: 'valor_hora_grupo', label: 'Valor aula em grupo (R$/h)', type: 'number' },
          { name: 'valor_mensal_fixo', label: 'Valor mensal fixo (R$) — ignora cálculo por hora', type: 'number' },
        ] : []),
      ];

  return (
    <Dialog title={title} onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
        {formFields.map(f => (
          <div key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>{f.label}</label>
            <input name={f.name} type={f.type} required={f.required}
              value={form[f.name]} autoComplete={f.name === 'senha' ? 'new-password' : undefined}
              onChange={e => upd(f.name, f.name === 'telefone' ? formatarTelefone(e.target.value) : e.target.value)}
              style={inputCss}/>
          </div>
        ))}
        {msg && <FeedbackMsg msg={msg}/>}
        <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
          {saving ? 'Salvando…' : isSenha ? 'Alterar senha' : isCreate ? 'Cadastrar aluno' : 'Salvar alterações'}
        </Button>
      </form>
    </Dialog>
  );
}

function RecorrenteModal({ alunos, onClose }) {
  const [form, setForm] = useState({ aluno_id: '', diaSemana: '', horario: '', dataInicio: '', dataFim: '', duracao: 60, tipo: 'presencial', modalidade: 'particular' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    axios.post(`${API_URL}/usuarios/${form.aluno_id}/aula-recorrente`, form, { withCredentials: true })
      .then(r => { setMsg(r.data.mensagem || 'Aulas criadas!'); setTimeout(onClose, 1500); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao agendar.'))
      .finally(() => setSaving(false));
  }

  const selectStyle2 = { height: 40, padding: '0 12px', fontSize: 14, width: '100%', background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', fontFamily: 'inherit', outline: 'none' };

  return (
    <Dialog title="Agendar aulas recorrentes" onClose={onClose}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
        <FormField label="Aluno">
          <AlunoSearch
            alunos={alunos}
            value={form.aluno_id}
            onChange={id => setForm(p => ({ ...p, aluno_id: id }))}
          />
        </FormField>
        {[
          { el: 'select', name: 'diaSemana', label: 'Dia da semana', options: ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'].map((d, i) => ({ value: i, label: d })) },
          { el: 'input', name: 'horario', label: 'Horário', type: 'time' },
          { el: 'input', name: 'dataInicio', label: 'Data início', type: 'date' },
          { el: 'input', name: 'dataFim', label: 'Data fim', type: 'date' },
          { el: 'input', name: 'duracao', label: 'Duração (min)', type: 'number', min: 15 },
          { el: 'select', name: 'tipo', label: 'Tipo', options: [{ value: 'presencial', label: 'Presencial' }, { value: 'online', label: 'Online' }] },
          { el: 'select', name: 'modalidade', label: 'Modalidade', options: [{ value: 'particular', label: 'Particular' }, { value: 'grupo', label: 'Grupo' }] },
        ].map(f => (
          <div key={f.name} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 12, fontWeight: 540, color: 'var(--text-secondary)' }}>{f.label}</label>
            {f.el === 'select' ? (
              <select value={form[f.name]} onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))} style={selectStyle2} required>
                <option value="">Selecione</option>
                {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            ) : (
              <input type={f.type} min={f.min} value={form[f.name]}
                onChange={e => setForm(p => ({ ...p, [f.name]: e.target.value }))} style={inputCss} required/>
            )}
          </div>
        ))}
        {msg && <FeedbackMsg msg={msg}/>}
        <Button variant="primary" size="lg" disabled={saving} style={{ width: '100%', marginTop: 4 }}>
          {saving ? 'Agendando…' : 'Criar aulas recorrentes'}
        </Button>
      </form>
    </Dialog>
  );
}

const POR_PAGINA = 20;

export default function Alunos() {
  const { tipo } = useAuth();
  const [alunos, setAlunos] = useState([]);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [modal, setModal] = useState(null);
  const [recorrenteOpen, setRecorrenteOpen] = useState(false);

  function carregar() {
    axios.get(`${API_URL}/alunos`, { withCredentials: true })
      .then(r => setAlunos(r.data)).catch(() => setAlunos([]));
  }

  useEffect(() => { carregar(); }, []);

  const filtrados = alunos
    .filter(a =>
      a.nome?.toLowerCase().includes(busca.toLowerCase()) ||
      a.email?.toLowerCase().includes(busca.toLowerCase()) ||
      a.login?.toLowerCase().includes(busca.toLowerCase())
    )
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

  const totalPaginas = Math.ceil(filtrados.length / POR_PAGINA);
  const paginados = filtrados.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  function handleBusca(v) { setBusca(v); setPagina(1); }

  function excluir(id) {
    if (!window.confirm('Excluir este aluno?')) return;
    axios.delete(`${API_URL}/alunos/${id}`, { withCredentials: true }).then(carregar);
  }

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Alunos" action={
        <Button size="sm" variant="primary" icon="plus" onClick={() => setModal({ tipo: 'criar', aluno: null })}>Novo</Button>
      }/>

      {/* Search */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-hairline)', borderRadius: 'var(--radius-md)' }}>
            <Icon name="search" size={15} style={{ color: 'var(--text-tertiary)' }}/>
            <input type="text" placeholder="Buscar aluno…" value={busca} onChange={e => handleBusca(e.target.value)}
              style={{ flex: 1, border: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', outline: 'none' }}/>
          </div>
          <Button size="md" variant="secondary" icon="calendar" onClick={() => setRecorrenteOpen(true)}>Recorrente</Button>
        </div>
      </div>

      <div style={{ padding: '0 var(--gap-3)' }}>
        {filtrados.length === 0 ? (
          <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
            <Icon name="users" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.35, color: 'var(--text-tertiary)' }}/>
            <div style={{ fontSize: 14, fontWeight: 540, color: 'var(--text-primary)' }}>
              {busca ? 'Nenhum aluno encontrado.' : 'Nenhum aluno cadastrado.'}
            </div>
          </Surface>
        ) : (
          <>
            <Surface>
              {paginados.map((a, i) => (
                <React.Fragment key={a.id}>
                  {i > 0 && <Divider/>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', padding: 'var(--pad-card)' }}>
                    {a.foto_url
                      ? <img src={a.foto_url} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}/>
                      : <Avatar name={a.nome} size={40}/>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 540, color: 'var(--text-primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{a.nome}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{a.email}</div>
                      {tipo === 'professora' && a.valor_mensal_fixo != null && (
                        <div style={{ fontSize: 11, fontFamily: '"Geist Mono", monospace', color: 'var(--accent)', fontWeight: 600 }}>
                          Mensal fixo: R$ {Number(a.valor_mensal_fixo).toFixed(2)}
                        </div>
                      )}
                      {tipo === 'professora' && !a.valor_mensal_fixo && (a.valor_hora_particular != null || a.valor_hora_grupo != null) && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: '"Geist Mono", monospace' }}>
                          {a.valor_hora_particular != null && `Part.: R$${Number(a.valor_hora_particular).toFixed(2)}`}
                          {a.valor_hora_particular != null && a.valor_hora_grupo != null && ' · '}
                          {a.valor_hora_grupo != null && `Grupo: R$${Number(a.valor_hora_grupo).toFixed(2)}`}
                        </div>
                      )}
                      {tipo === 'professora' && !a.valor_mensal_fixo && a.valor_hora_particular == null && a.valor_hora_grupo == null && a.valor_hora != null && (
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: '"Geist Mono", monospace' }}>
                          R$ {Number(a.valor_hora).toFixed(2)}/h
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <IconButton icon="user" size={28} variant="ghost" onClick={() => setModal({ tipo: 'detalhes', aluno: a })} label="Ver detalhes"/>
                      <IconButton icon="edit" size={28} variant="ghost" onClick={() => setModal({ tipo: 'editar', aluno: a })} label="Editar"/>
                      <IconButton icon="settings" size={28} variant="ghost" onClick={() => setModal({ tipo: 'senha', aluno: a })} label="Senha"/>
                      <IconButton icon="close" size={28} variant="ghost" style={{ color: 'var(--danger)' }} onClick={() => excluir(a.id)} label="Excluir"/>
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </Surface>

            {totalPaginas > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--gap-2)', marginTop: 'var(--gap-2)' }}>
                <IconButton icon="chevron-left" size={32} variant="surface" disabled={pagina === 1} onClick={() => setPagina(p => p - 1)} label="Anterior"/>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', minWidth: 70, textAlign: 'center' }}>
                  {pagina} / {totalPaginas}
                </span>
                <IconButton icon="chevron-right" size={32} variant="surface" disabled={pagina === totalPaginas} onClick={() => setPagina(p => p + 1)} label="Próxima"/>
              </div>
            )}
          </>
        )}
      </div>

      {modal && (
        <AlunoModal
          tipo={modal.tipo} aluno={modal.aluno} allAlunos={alunos}
          isProfessora={tipo === 'professora'}
          onClose={() => setModal(null)} onSaved={carregar}
        />
      )}
      {recorrenteOpen && <RecorrenteModal alunos={alunos} onClose={() => setRecorrenteOpen(false)}/>}
    </div>
  );
}
